import { WorkspaceInvitationsApi } from '@up2stay/up2promo-api-client';
import { Badge, Button, Card } from '@up2stay/up2promo-ui';
import type { AxiosError } from 'axios';
import { Building2, Check, Loader2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { ListEmptyState } from '@/core/components/layout';
import { useErrorMessage } from '@/core/hooks/use-error-message';
import { usePendingInvitations } from '@/core/hooks/use-pending-invitations';
import { useWorkspaces } from '@/core/hooks/use-workspaces';
import { useDictionary } from '@/core/providers/dictionary-provider';

const INVITE_LABEL_CLASS =
  'text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground';

export function UserInvitationsPanel() {
  const dict = useDictionary();
  const invitationDict = dict.workspace.invitation;
  const { data: invitations, isLoading, refetch } = usePendingInvitations();
  const { refetch: refetchWorkspaces } = useWorkspaces();
  const getErrorMessage = useErrorMessage();
  const location = useLocation();
  const processedActionRef = useRef<string | null>(null);

  const [processingAcceptIds, setProcessingAcceptIds] = useState<Set<string>>(
    new Set(),
  );
  const [processingRejectIds, setProcessingRejectIds] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const membershipId = searchParams.get('membershipId') ?? null;
    const action = searchParams.get('action') ?? null;
    const actionKey =
      membershipId && action ? `${membershipId}-${action}` : null;

    if (
      !actionKey ||
      processedActionRef.current === actionKey ||
      isLoading ||
      !invitations
    ) {
      return;
    }

    const invitation = invitations.find((inv) => inv.id === membershipId);
    if (invitation && membershipId) {
      processedActionRef.current = actionKey;
      const processAction = async () => {
        const url = new URL(window.location.href);
        url.searchParams.delete('membershipId');
        url.searchParams.delete('action');
        window.history.replaceState({}, '', url.toString());

        if (action === 'accept' && membershipId) {
          setProcessingAcceptIds((prev) => new Set(prev).add(membershipId));
          try {
            await WorkspaceInvitationsApi.acceptInvitation({
              path: { membershipId },
            });
            await refetchWorkspaces();
            await refetch();
            toast.success(invitationDict.accept.success);
          } catch (err) {
            const error = err as AxiosError<{
              errors?: Record<string, string>;
            }>;
            const backendErrors = error.response?.data?.errors;
            const firstCode =
              (backendErrors &&
                (Object.values(backendErrors)[0] as string | undefined)) ||
              undefined;
            toast.error(
              getErrorMessage(firstCode) || invitationDict.errors.acceptFailed,
            );
            processedActionRef.current = null;
          } finally {
            setProcessingAcceptIds((prev) => {
              const next = new Set(prev);
              next.delete(membershipId);
              return next;
            });
          }
        } else if (action === 'decline' && membershipId) {
          setProcessingRejectIds((prev) => new Set(prev).add(membershipId));
          try {
            await WorkspaceInvitationsApi.rejectInvitation({
              path: { membershipId },
            });
            await refetch();
            toast.success(invitationDict.reject.success);
          } catch (err) {
            const error = err as AxiosError<{
              errors?: Record<string, string>;
            }>;
            const backendErrors = error.response?.data?.errors;
            const firstCode =
              (backendErrors &&
                (Object.values(backendErrors)[0] as string | undefined)) ||
              undefined;
            toast.error(
              getErrorMessage(firstCode) || invitationDict.errors.rejectFailed,
            );
            processedActionRef.current = null;
          } finally {
            setProcessingRejectIds((prev) => {
              const next = new Set(prev);
              next.delete(membershipId);
              return next;
            });
          }
        }
      };
      void processAction();
    }
  }, [
    location.search,
    isLoading,
    invitations,
    refetchWorkspaces,
    refetch,
    getErrorMessage,
    invitationDict,
  ]);

  const handleAccept = async (membershipId: string) => {
    setProcessingAcceptIds((prev) => new Set(prev).add(membershipId));
    try {
      await WorkspaceInvitationsApi.acceptInvitation({
        path: { membershipId },
      });
      await refetchWorkspaces();
      await refetch();
      toast.success(invitationDict.accept.success);
    } catch (err) {
      const error = err as AxiosError<{ errors?: Record<string, string> }>;
      const backendErrors = error.response?.data?.errors;
      const firstCode =
        (backendErrors &&
          (Object.values(backendErrors)[0] as string | undefined)) ||
        undefined;
      toast.error(
        getErrorMessage(firstCode) || invitationDict.errors.acceptFailed,
      );
    } finally {
      setProcessingAcceptIds((prev) => {
        const next = new Set(prev);
        next.delete(membershipId);
        return next;
      });
    }
  };

  const handleReject = async (membershipId: string) => {
    setProcessingRejectIds((prev) => new Set(prev).add(membershipId));
    try {
      await WorkspaceInvitationsApi.rejectInvitation({
        path: { membershipId },
      });
      await refetch();
      toast.success(invitationDict.reject.success);
    } catch (err) {
      const error = err as AxiosError<{ errors?: Record<string, string> }>;
      const backendErrors = error.response?.data?.errors;
      const firstCode =
        (backendErrors &&
          (Object.values(backendErrors)[0] as string | undefined)) ||
        undefined;
      toast.error(
        getErrorMessage(firstCode) || invitationDict.errors.rejectFailed,
      );
    } finally {
      setProcessingRejectIds((prev) => {
        const next = new Set(prev);
        next.delete(membershipId);
        return next;
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!invitations?.length) {
    return (
      <ListEmptyState
        icon={<Building2 className="h-6 w-6" aria-hidden />}
        title={invitationDict.list.empty}
      />
    );
  }

  return (
    <Card className="min-w-0 overflow-hidden">
      <div className="divide-y divide-border">
        {invitations.map((invitation) => {
          const isAccepting = processingAcceptIds.has(invitation.id);
          const isRejecting = processingRejectIds.has(invitation.id);
          return (
            <div key={invitation.id} className="flex flex-col gap-4 p-5 sm:p-6">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="flex flex-col gap-1">
                  <p className={INVITE_LABEL_CLASS}>
                    {invitationDict.list.table.workspace}
                  </p>
                  <p className="text-[13.5px] font-semibold leading-snug text-foreground">
                    {invitation.workspace?.name ||
                      invitation.workspaceId ||
                      '-'}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <p className={INVITE_LABEL_CLASS}>
                    {invitationDict.list.table.role}
                  </p>
                  <Badge variant="outline" className="mt-0.5 w-fit">
                    {invitation.role === 'admin' || invitation.role === 'member'
                      ? dict.workspace.create.inviteTeam.roles[invitation.role]
                      : invitation.role}
                  </Badge>
                </div>
                <div className="flex flex-col gap-1">
                  <p className={INVITE_LABEL_CLASS}>
                    {invitationDict.list.table.invitedBy}
                  </p>
                  <p className="text-[13.5px] leading-relaxed text-muted-foreground">
                    {invitation.invitedByUser
                      ? invitation.invitedByUser.firstName &&
                        invitation.invitedByUser.lastName
                        ? `${invitation.invitedByUser.firstName} ${invitation.invitedByUser.lastName}`
                        : invitation.invitedByUser.email || '-'
                      : invitation.invitedBy || '-'}
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleReject(invitation.id)}
                  disabled={isRejecting}
                  className="sm:min-w-28"
                >
                  {isRejecting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <X className="mr-2 h-4 w-4" />
                  )}
                  {invitationDict.list.actions.decline}
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleAccept(invitation.id)}
                  disabled={isAccepting}
                  className="sm:min-w-28"
                >
                  {isAccepting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="mr-2 h-4 w-4" />
                  )}
                  {invitationDict.list.actions.accept}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
